
import { IgGlobalFilters, insightsiqPlatformWorkIdMap, SORT_OPTIONS, YtGlobalFilters } from "@entities/global-discovery/constants";
import { useGetGlobalDiscoveryStore } from "@entities/global-discovery/store/useGetGlobalDiscoveryStoreV2";
import { MultiListFilter } from "@entities/global-discovery/ui/Filters/MultiListFilter";
import { RangeFilter } from "@entities/global-discovery/ui/Filters/RangeFilter";
import { Button, Divider, Flex, Select, Space } from "antd";
import { Search, SlidersHorizontal } from "lucide-react";
import { useEffect, useMemo } from "react";
import { SearchBar } from "../SearchBar";
import styles from "./SearchHeader.module.scss";

type SearchHeaderProps = {
  platformId: string;
  onToggleAdvanced: () => void;
  isAdvancedFiltersOpen?: boolean;
  handleSaveFilters: () => void;
  hideFilterButton?: boolean;
  handlePlatformChange: (newPlatformId: string) => void;
};

export const SearchHeader = ({
  platformId,
  onToggleAdvanced,
  isAdvancedFiltersOpen = false,
  handleSaveFilters,
  hideFilterButton = false,
  handlePlatformChange,
}: SearchHeaderProps) => {
  const { selectedFilters, setSelectedFilter, resetSelectedFilters } =
    useGetGlobalDiscoveryStore();

  const followerCountFilterConfig =
    platformId === insightsiqPlatformWorkIdMap["instagram"]
      ? IgGlobalFilters.find((filter) => filter.facetName === "follower_count")
      : YtGlobalFilters.find(
          (filter) => filter.facetName === "subscriber_count"
        );

  const creatorLocationFilterConfig =
    platformId === insightsiqPlatformWorkIdMap["instagram"]
      ? IgGlobalFilters.find(
          (filter) => filter.facetName === "creator_locations"
        )
      : YtGlobalFilters.find(
          (filter) => filter.facetName === "creator_locations"
        );

  // Get platform filters for mapping facetName to title
  const platformFilters = useMemo(() => {
    if (!platformId) return [];
    const isInstagram = platformId === insightsiqPlatformWorkIdMap["instagram"];
    const isYoutube = platformId === insightsiqPlatformWorkIdMap["youtube"];
    if (isInstagram) return IgGlobalFilters;
    if (isYoutube) return YtGlobalFilters;
    return [];
  }, [platformId]);

  // Create a map of facetName to title
  const facetNameToTitle = useMemo(() => {
    const map = new Map<string, string>();
    platformFilters.forEach((filter) => {
      map.set(filter.facetName, filter.title);
    });
    return map;
  }, [platformFilters]);

  // Calculate active filters count using the same logic as chips
  const activeAdvancedFiltersCount = useMemo(() => {
    const ft = selectedFilters.filterTags || {};
    const processedKeys = new Set<string>();
    let count = 0;

    Object.entries(ft).forEach(([k, v]) => {
      // Skip sort_by and already processed keys
      if (k === "sort_by" || processedKeys.has(k)) return;

      // Handle range filters (min/max)
      if (k.endsWith("_min") || k.endsWith("_max")) {
        const baseKey = k.replace(/_(min|max)$/, "");
        if (processedKeys.has(baseKey)) return;

        const minKey = `${baseKey}_min`;
        const maxKey = `${baseKey}_max`;
        const minVal = ft[minKey];
        const maxVal = ft[maxKey];

        // Count as 1 if at least one value exists
        if (
          (minVal !== undefined && minVal !== null) ||
          (maxVal !== undefined && maxVal !== null)
        ) {
          count++;
        }
        processedKeys.add(baseKey);
        return;
      }

      // Handle SelectRangeFilter (has _min suffix for percentage)
      const minKey = `${k}_min`;
      if (ft[minKey] !== undefined && ft[minKey] !== null) {
        count++;
        processedKeys.add(k);
        return;
      }

      // Handle array values (multi-select, tags) - count as 1 filter
      if (Array.isArray(v) && v.length > 0) {
        count++;
        processedKeys.add(k);
        return;
      }

      // Handle single values
      if (v !== undefined && v !== null) {
        count++;
        processedKeys.add(k);
      }
    });
    return count;
  }, [selectedFilters]);

  const handleSortChange = (value: string) => {
    const selected = SORT_OPTIONS.find(
      (opt) => JSON.stringify(opt.value) === value
    );
    if (!selected) return;
    setSelectedFilter((prev) => ({
      ...prev,
      sort_by: selected.value,
    }));
  };

  const filteredSortOptions = useMemo(() => {
    const hasTopic =
      selectedFilters.filterTags?.topics &&
      selectedFilters.filterTags?.topics?.length > 0;
    if (hasTopic) {
      return SORT_OPTIONS;
    }
    return SORT_OPTIONS.filter(
      (opt) => opt.value.field !== "TOPIC_RELEVANCE"
    );
  }, [selectedFilters.filterTags?.topics]);

  useEffect(() => {
    const currentSort = (selectedFilters as any)?.sort_by;
    const hasTopic =
      selectedFilters.filterTags?.topics &&
      selectedFilters.filterTags?.topics?.length > 0;

    if (
      !hasTopic &&
      currentSort &&
      currentSort.field === "TOPIC_RELEVANCE"
    ) {
      handleSortChange(JSON.stringify(SORT_OPTIONS[0].value));
    }
  }, [selectedFilters.filterTags?.topics, selectedFilters]);

  const sortValue = useMemo(() => {
    const current = (selectedFilters as any)?.sort_by;
    if (!current) return undefined;
    const match = SORT_OPTIONS.find((opt) => opt.value.field === current.field);
    return match ? JSON.stringify(match.value) : undefined;
  }, [selectedFilters]);

  // Generate chips from filterTags (excluding sort_by)
  const chips = useMemo(() => {
    const ft = selectedFilters.filterTags || {};
    const items: { key: string; label: string; index?: number }[] = [];
    const processedKeys = new Set<string>();

    Object.entries(ft).forEach(([k, v]) => {
      // Skip sort_by and already processed keys
      if (k === "sort_by" || processedKeys.has(k)) return;

      const title = facetNameToTitle.get(k) || k;

      // Handle range filters (min/max)
      if (k.endsWith("_min") || k.endsWith("_max")) {
        const baseKey = k.replace(/_(min|max)$/, "");
        if (processedKeys.has(baseKey)) return;

        const minKey = `${baseKey}_min`;
        const maxKey = `${baseKey}_max`;
        const minVal = ft[minKey];
        const maxVal = ft[maxKey];

        if (
          minVal !== undefined &&
          minVal !== null &&
          maxVal !== undefined &&
          maxVal !== null
        ) {
          items.push({
            key: baseKey,
            label: `${
              facetNameToTitle.get(baseKey) || baseKey
            }: ${minVal} - ${maxVal}`,
          });
        } else if (minVal !== undefined && minVal !== null) {
          items.push({
            key: baseKey,
            label: `${facetNameToTitle.get(baseKey) || baseKey}: ${minVal}+`,
          });
        } else if (maxVal !== undefined && maxVal !== null) {
          items.push({
            key: baseKey,
            label: `${
              facetNameToTitle.get(baseKey) || baseKey
            }: up to ${maxVal}`,
          });
        }
        processedKeys.add(baseKey);
        return;
      }

      // Handle SelectRangeFilter (has _min suffix for percentage)
      const minKey = `${k}_min`;
      if (ft[minKey] !== undefined && ft[minKey] !== null) {
        const minPercentage = ft[minKey];
        items.push({
          key: k,
          label: `${title}: ${String(v)} (${minPercentage}%)`,
        });
        processedKeys.add(k);
        return;
      }

      // Handle array values (multi-select, tags)
      if (Array.isArray(v)) {
        v.forEach((label, idx) => {
          items.push({
            key: k,
            label: `${title}: ${String(label)}`,
            index: idx,
          });
        });
        processedKeys.add(k);
        return;
      }

      // Handle single values
      if (v !== undefined && v !== null) {
        items.push({ key: k, label: `${title}: ${String(v)}` });
        processedKeys.add(k);
      }
    });
    return items;
  }, [selectedFilters, facetNameToTitle]);

  const handleRemoveChip = (key: string, index?: number) => {
    setSelectedFilter((prev) => {
      const next = { ...prev };
      // Update filterTags
      if (next.filterTags) {
        const tagVal = next.filterTags[key];
        if (Array.isArray(tagVal)) {
          const updated = tagVal.filter((_, idx) => idx !== index);
          if (updated.length) {
            next.filterTags = { ...next.filterTags, [key]: updated };
          } else {
            const { [key]: _, ...rest } = next.filterTags;
            next.filterTags = rest;
          }
        } else {
          // Handle range filters: remove both _min and _max
          const minKey = `${key}_min`;
          const maxKey = `${key}_max`;
          const {
            [key]: _,
            [minKey]: __,
            [maxKey]: ___,
            ...rest
          } = next.filterTags;
          next.filterTags = rest;
        }
      }
      // Update actual filter values
      const filterVal = next[key];
      if (Array.isArray(filterVal)) {
        const updated = filterVal.filter((_, idx) => idx !== index);
        if (updated.length) {
          next[key] = updated;
        } else {
          const { [key]: _, ...rest } = next;
          return { ...rest, filterTags: next.filterTags };
        }
      } else {
        const { [key]: _, ...rest } = next;
        return { ...rest, filterTags: next.filterTags };
      }
      return next;
    });
    // handleSaveFilters();
  };

  return (
    <div className={styles.container}>
      {/* Header Section */}
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerIcon}>
            <Search size={24} />
          </div>
          <div>
            <h2 className={styles.headerTitle}>Smart Search</h2>
            <p className={styles.headerSubtitle}>
              Find the perfect creators for your campaigns.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        <SearchBar
          handleSaveFilters={handleSaveFilters}
          handlePlatformChange={handlePlatformChange}
        />
        <Divider className={styles.divider} />
        <Flex justify="space-between" align="center">
          <Flex gap={16}>
            <RangeFilter
              filter={followerCountFilterConfig!}
              showTitle={false}
            />
            <MultiListFilter
              filter={creatorLocationFilterConfig!}
              showTitle={false}
            />
            {hideFilterButton ? (
              <></>
            ) : (
              <div
                className={`${styles.advanceFiltersButton} ${
                  isAdvancedFiltersOpen || activeAdvancedFiltersCount > 0
                    ? styles.active
                    : ""
                }`}
                onClick={onToggleAdvanced}
              >
                <SlidersHorizontal size={16} />
                <span>Advance Filter's</span>
                {activeAdvancedFiltersCount > 0 && (
                  <span className={styles.filterBadge}>
                    {activeAdvancedFiltersCount}
                  </span>
                )}
              </div>
            )}
          </Flex>

          <Select
            className={styles.sortSelect}
            options={filteredSortOptions.map((opt) => ({
              label: opt.label,
              value: JSON.stringify(opt.value),
            }))}
            placeholder="Sort by"
            value={sortValue}
            onChange={handleSortChange}
          />
        </Flex>
        <Divider className={styles.divider} />
        {/* Filter Chips and Search Button */}
        <Flex justify="space-between" align="start">
          <Space size={8} align="start" style={{ width: "90%" }}>
            {chips.length > 0 ? (
              <>
                <div className={styles.chipsRow}>
                  {chips.map((chip) => (
                    <span
                      key={`${chip.key}-${chip.label}-${chip.index ?? ""}`}
                      className={styles.chip}
                    >
                      {chip.label}
                      <button
                        type="button"
                        className={styles.chipRemove}
                        onClick={() => handleRemoveChip(chip.key, chip.index)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <button
                  type="button"
                  className={styles.clearAllButton}
                  onClick={resetSelectedFilters}
                >
                  Clear all
                </button>
              </>
            ) : (
              <div style={{ visibility: "hidden" }}></div>
            )}
          </Space>
          <Button onClick={() => handleSaveFilters()} type="primary">
            Search
          </Button>
        </Flex>
      </div>
    </div>
  );
};
